// 37_VOICE_TO_TEXT (doc 37/01 §3.3) -- the voice-to-text controller of one
// composer: record -> upload -> transcribe -> hand the text to the host,
// which appends it to the Student's draft. The Student always sends it
// themselves; nothing here sends a Tutor turn.
//
// One reactive `state` the composer renders (V405's idle / recording /
// transcribing / ready / error):
//   phase      'idle' | 'recording' | 'transcribing' | 'ready' | 'error'
//   elapsed    whole seconds recorded so far (recording)
//   durationSec the recording's length (ready)
//   noteKey    `aiTutorSetup.chat.voice.errors.<noteKey>` (error)
//
// Rules (doc 37/00 D-11, D-13):
// - The upload URL is asked for while the Student speaks, so it is ready at
//   stop. An unused one simply expires.
// - Nothing is ever retried: one stop = at most one upload and one
//   transcription request. A failed or consumed filename is never reused;
//   the way back is a new recording.
// - Every async step checks the generation it started in: a cancel, a
//   session change or leaving the page makes a late answer a no-op, and the
//   microphone is released on each of them.
// - The draft is touched only by `onTranscript`, on a non-empty transcript.

import Vue from 'vue'
import * as defaultRecorder from './voiceRecorder'
import { isOnline as browserOnline } from '../connectivity'

const RESTARTABLE = ['idle', 'ready', 'error']

// Every network step after the Student's stop is bounded, so the transcribing
// state always ends (doc 37/00 D-13: a timeout returns to the composer with a
// note; nothing is retried). The transcription bound sits above Subway's own
// 75 s provider timeout, so Subway answers first whenever it can.
export const VOICE_UPLOAD_URL_WAIT_MS = 30000
export const VOICE_UPLOAD_TIMEOUT_MS = 30000
export const VOICE_TRANSCRIPTION_WAIT_MS = 90000
const UPLOAD_TIMED_OUT = { status: 0, body: 'timeout' }
const TRANSCRIPTION_TIMED_OUT = {
  status: 504,
  body: { code: 'AI_TUTOR_VOICE_UNAVAILABLE' },
}

// A transcript with no words: empty, or only bracketed sound labels such as
// «[صدای محیط]» (speech-to-text-api.md §3).
const SOUND_LABELS_ONLY = /^\s*(\[[^\]]*\]\s*)+$/

export function isNothingHeard(text) {
  const value = typeof text === 'string' ? text.trim() : ''
  return !value || SOUND_LABELS_ONLY.test(value)
}

const REASON_NOTES = {
  unsupported: 'unsupported',
  denied: 'denied',
  no_device: 'noDevice',
  busy: 'busy',
  interrupted: 'interrupted',
  empty: 'nothingHeard',
  too_large: 'tooLong',
}

/**
 * The note for a failure, or '' for one the Student caused on purpose.
 * Recorder failures carry `{reason}`; Subway and storage failures `{status,
 * body}` (api.js); a fetch that never got an answer (network, CORS) throws a
 * TypeError.
 */
export function noteKeyForError(err) {
  if (err && typeof err.reason === 'string') {
    if (err.reason === 'cancelled') return ''
    return REASON_NOTES[err.reason] || 'recordAgain'
  }
  const status = err && typeof err.status === 'number' ? err.status : null
  if (status === null || status === 0) return 'network'
  const code = err.body && typeof err.body === 'object' ? err.body.code : null
  if (status === 429) return 'rateLimited'
  if (
    code === 'AI_TUTOR_VOICE_UNAVAILABLE' ||
    // not offered to this acting role (a non-Student context)
    code === 'AI_TUTOR_BOARD_STUDENT_ONLY' ||
    status === 404 ||
    status >= 500
  ) {
    return 'unavailable'
  }
  return 'recordAgain'
}

/**
 * @param {object} options
 * @param {object} options.api -- createVoiceUploadUrl, uploadToPresignedPost,
 *   createVoiceTranscription (api.js)
 * @param {(text: string) => void} options.onTranscript
 * @param {() => ('fa'|'en')} [options.uiLocale]
 * @param {object} [options.recorder] -- voiceRecorder.js
 * @param {object} [options.env] -- window
 */
export function createVoiceInput({
  api,
  onTranscript,
  uiLocale = () => 'fa',
  recorder = defaultRecorder,
  env = typeof window !== 'undefined' ? window : undefined,
  now = () => Date.now(),
  timers = {
    setInterval: (fn, ms) => setInterval(fn, ms),
    clearInterval: id => clearInterval(id),
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: id => clearTimeout(id),
  },
  maxSeconds = defaultRecorder.VOICE_MAX_SECONDS,
  isOnline = browserOnline,
} = {}) {
  const state = Vue.observable({
    phase: 'idle',
    elapsed: 0,
    durationSec: 0,
    noteKey: '',
  })

  let gen = 0
  let starting = false
  let destroyed = false
  let handle = null
  let timer = null
  let startedAt = 0
  // { format, outcome: Promise<{ok, upload?, error?}> }
  let prefetch = null

  function clearTimer() {
    if (timer !== null) {
      timers.clearInterval(timer)
      timer = null
    }
  }

  function releaseRecording() {
    clearTimer()
    if (handle) {
      const current = handle
      handle = null
      current.cancel()
    }
    prefetch = null
  }

  function fail(err) {
    releaseRecording()
    const noteKey = noteKeyForError(err)
    state.elapsed = 0
    state.noteKey = noteKey
    state.phase = noteKey ? 'error' : 'idle'
  }

  // `work` or `timeoutError` after `ms`, whichever comes first; on the
  // timeout the request is aborted where it can be.
  function bounded(work, ms, timeoutError, controller) {
    const set = timers.setTimeout || ((fn, delay) => setTimeout(fn, delay))
    const clear = timers.clearTimeout || (id => clearTimeout(id))
    let id = null
    const limit = new Promise((resolve, reject) => {
      id = set(() => {
        if (controller) controller.abort()
        reject(timeoutError)
      }, ms)
    })
    return Promise.race([work, limit]).then(
      value => {
        clear(id)
        return value
      },
      error => {
        clear(id)
        throw error
      }
    )
  }

  function askUploadUrl(fileFormat) {
    // Settled here, so an unused prefetch never becomes an unhandled
    // rejection.
    return Promise.resolve()
      .then(() => api.createVoiceUploadUrl({ fileFormat }))
      .then(
        upload => ({ ok: true, upload }),
        error => ({ ok: false, error })
      )
  }

  async function start() {
    if (destroyed || starting || !RESTARTABLE.includes(state.phase)) return
    // P40 QA-29: a recording made offline could never be transcribed, so it
    // is refused at once with the network note -- not discovered at Stop.
    if (!isOnline()) {
      gen += 1
      fail({ status: 0, body: 'offline' })
      return
    }
    gen += 1
    const mine = gen
    starting = true
    state.noteKey = ''
    state.durationSec = 0
    state.elapsed = 0
    state.phase = 'idle'
    let opened
    try {
      opened = await recorder.startRecording(env)
    } catch (err) {
      starting = false
      if (mine === gen && !destroyed) fail(err)
      return
    }
    starting = false
    if (mine !== gen || destroyed) {
      // Cancelled, reset or left while the permission prompt was open.
      opened.cancel()
      return
    }
    handle = opened
    startedAt = now()
    prefetch = {
      format: opened.fileFormat,
      outcome: askUploadUrl(opened.fileFormat),
    }
    // P40 QA-29: the upload URL is asked for as recording starts; when that
    // request cannot reach Subway at all, the recording stops right away with
    // the network note instead of running on until Stop.
    prefetch.outcome.then(result => {
      if (
        !result.ok &&
        mine === gen &&
        handle === opened &&
        noteKeyForError(result.error) === 'network'
      ) {
        fail(result.error)
      }
    })
    opened.onInterrupted(() => {
      if (mine === gen && handle === opened) fail({ reason: 'interrupted' })
    })
    state.phase = 'recording'
    timer = timers.setInterval(() => {
      if (mine !== gen) return
      const seconds = Math.floor((now() - startedAt) / 1000)
      state.elapsed = Math.min(maxSeconds, Math.max(0, seconds))
      if (seconds >= maxSeconds) stop()
    }, 1000)
  }

  async function stop() {
    if (state.phase !== 'recording' || !handle) return
    const mine = gen
    const current = handle
    handle = null
    clearTimer()
    state.phase = 'transcribing'
    const pending = prefetch
    prefetch = null
    try {
      const recording = await current.stop()
      if (mine !== gen) return
      state.durationSec = Math.min(
        maxSeconds,
        Math.max(1, Math.round(recording.durationMs / 1000))
      )

      let upload
      const early = pending
        ? await bounded(
            pending.outcome,
            VOICE_UPLOAD_URL_WAIT_MS,
            UPLOAD_TIMED_OUT
          )
        : null
      if (mine !== gen) return
      if (early && !early.ok) throw early.error
      if (early && pending.format === recording.fileFormat) {
        upload = early.upload
      } else {
        // The recorder produced another container than it announced (or
        // nothing was prefetched): a URL for what is really there.
        upload = await bounded(
          api.createVoiceUploadUrl({ fileFormat: recording.fileFormat }),
          VOICE_UPLOAD_URL_WAIT_MS,
          UPLOAD_TIMED_OUT
        )
        if (mine !== gen) return
      }

      const controller =
        typeof AbortController === 'function' ? new AbortController() : null
      await bounded(
        api.uploadToPresignedPost(upload, recording.blob, {
          signal: controller ? controller.signal : undefined,
        }),
        VOICE_UPLOAD_TIMEOUT_MS,
        UPLOAD_TIMED_OUT,
        controller
      )
      if (mine !== gen) return

      const result = await bounded(
        api.createVoiceTranscription({
          filename: upload.filename,
          uiLocale: uiLocale() === 'fa' ? 'fa' : 'en',
        }),
        VOICE_TRANSCRIPTION_WAIT_MS,
        TRANSCRIPTION_TIMED_OUT
      )
      if (mine !== gen) return

      const text =
        result && typeof result.text === 'string' ? result.text.trim() : ''
      if (isNothingHeard(text)) {
        fail({ reason: 'empty' })
        return
      }
      onTranscript(text)
      state.noteKey = ''
      state.phase = 'ready'
    } catch (err) {
      if (mine === gen && !destroyed) fail(err)
    }
  }

  // The Student's × while recording: the recording is thrown away.
  function cancel() {
    if (state.phase !== 'recording') return
    gen += 1
    releaseRecording()
    state.elapsed = 0
    state.phase = 'idle'
  }

  // The ready card's × or a dismissed note: back to the plain composer.
  function dismiss() {
    if (state.phase !== 'ready' && state.phase !== 'error') return
    state.noteKey = ''
    state.durationSec = 0
    state.phase = 'idle'
  }

  // Another conversation: whatever was under way belongs to the old one.
  function reset() {
    gen += 1
    releaseRecording()
    state.elapsed = 0
    state.durationSec = 0
    state.noteKey = ''
    state.phase = 'idle'
  }

  // The page is going away: release the microphone, ignore every late
  // answer.
  function destroy() {
    destroyed = true
    gen += 1
    releaseRecording()
  }

  return { state, start, stop, cancel, dismiss, reset, destroy }
}
